import type { LocalRow, SeedTables } from '../types/localDb.types'
import { roleId, seedAudit } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'

/**
 * Konfigurasi Workflow Studio WF-REQ-01 (Material Request RM — SPK & Tembakau) — disalin dari
 * `docs/tl-doc/12B-SERVICE-DATA/JAL_12B-SERVICE-DATA-WF-REQ-01.md` §1–§8 (label [C]).
 * Tabel email, tabbing, SLA, dan cross-workflow tidak di-seed karena tidak dijalankan local mode.
 *
 * Penyimpangan yang dicatat di laporan C06:
 * - TL §3 hanya mendaftarkan Admin WH RM untuk `ACT_REQ_ASSIGN`; BA REQ-02A menyebut "Admin WH RM, Supervisor, Kadiv
 *   (act)" → Supervisor dan Kadiv ditambahkan sebagai actor/eligibility (BA menang untuk aturan bisnis pengguna).
 * - WFL tasking P03.F01 memuat dua diagram dan nama status berbeda (REQ_CONFIRMED / REQ_PICKED …); TL 12B
 *   (REQ_PENDING_ASSIGN / REQ_PICKING_ASSIGNED / REQ_READY_HANDOVER) dipakai karena sama dengan badge BA.
 */

const WORKFLOW_NO = 5
export const WF_REQ_01_ID = seedId(SEED_GROUP.workflow, WORKFLOW_NO)
const scoped = (n: number) => WORKFLOW_NO * 1000 + n

const STATUSES = [
  ['REQ_DRAFT', 'Draft', 1, true, false, '#64748B', 'Draft permintaan material oleh Tim Produksi'],
  ['REQ_PENDING_ASSIGN', 'Menunggu Penugasan', 2, false, false, '#F59E0B', 'Permintaan terkirim ke Gudang RM'],
  ['REQ_PICKING_ASSIGNED', 'Picking Assigned', 3, false, false, '#06B6D4', 'Tugas picking diterbitkan ke Operator RM'],
  ['REQ_READY_HANDOVER', 'Ready for Handover', 4, false, false, '#8B5CF6', 'Picking selesai, siap diserahterimakan'],
  ['REQ_COMPLETED', 'Completed', 5, false, true, '#10B981', 'Semua serah terima telah diterima Produksi'],
  ['REQ_CANCELLED', 'Cancelled', 6, false, true, '#94A3B8', 'Permintaan dibatalkan']
] as const

export type ReqStatusCode = (typeof STATUSES)[number][0]

export const reqStatusId = (code: ReqStatusCode) =>
  seedId(SEED_GROUP.wmlStatus, scoped(STATUSES.findIndex(([c]) => c === code) + 1))

const ACTORS = [
  ['ACT_REQ_PRODUKSI', 'Tim Produksi', 'tim_produksi', { created_by: { _eq: '$CURRENT_USER.id' } }],
  ['ACT_REQ_ADMIN_RM', 'Admin WH RM', 'admin_wh_rm', null],
  ['ACT_REQ_OP_RM', 'Operator WH RM', 'operator_wh_rm', null],
  ['ACT_REQ_SPV_RM', 'WH RM Supervisor', 'wh_rm_supervisor', null],
  ['ACT_REQ_KADIV_RM', 'Kadiv WH RM', 'kadiv_wh_rm', null]
] as const

export type ReqActorCode = (typeof ACTORS)[number][0]

export const reqActorId = (code: ReqActorCode) =>
  seedId(SEED_GROUP.wmlActor, scoped(ACTORS.findIndex(([c]) => c === code) + 1))

const COMPONENTS = [
  ['CMP_REQ_SUBMIT', 'Tombol Kirim Request', 'ACTION', { color: '#3B82F6', variant: 'primary' }],
  ['CMP_REQ_ASSIGN_PICK', 'Tombol Terbitkan Tugas Picking', 'ACTION', {}],
  ['CMP_REQ_FINISH_PICK', 'Tombol Selesai Picking', 'ACTION', {}],
  ['CMP_REQ_ACCEPT_HND', 'Tombol Terima Handover', 'ACTION', {}],
  ['CMP_REQ_TRIGGER_PR', 'Tombol Submit dengan Shortage', 'ACTION', {}],
  ['CMP_REQ_CANCEL', 'Tombol Batalkan Request', 'ACTION', { color: '#EF4444' }],
  ['CMP_REQ_PANEL_HEADER', 'Panel Header Request', 'PANEL', {}],
  ['CMP_REQ_PANEL_ITEMS', 'Panel Detail Material', 'PANEL', {}],
  ['CMP_REQ_PANEL_HANDOVER', 'Panel Serah Terima (Handover)', 'PANEL', {}]
] as const

type ComponentCode = (typeof COMPONENTS)[number][0]

const componentId = (code: ComponentCode) =>
  seedId(SEED_GROUP.wmlComponent, scoped(COMPONENTS.findIndex(([c]) => c === code) + 1))

/** [name, from, to, component, is_need_reason, is_reason_required, entity_patch_mapping]. */
const ACTIONS: [
  string,
  ReqStatusCode,
  ReqStatusCode,
  ComponentCode,
  boolean,
  boolean,
  Record<string, unknown> | null
][] = [
  ['ACT_REQ_SEND', 'REQ_DRAFT', 'REQ_PENDING_ASSIGN', 'CMP_REQ_SUBMIT', false, false, { has_shortage: false }],
  [
    'ACT_REQ_SHORTAGE_PR',
    'REQ_DRAFT',
    'REQ_PENDING_ASSIGN',
    'CMP_REQ_TRIGGER_PR',
    false,
    false,
    { has_shortage: true }
  ],
  ['ACT_REQ_ASSIGN', 'REQ_PENDING_ASSIGN', 'REQ_PICKING_ASSIGNED', 'CMP_REQ_ASSIGN_PICK', false, false, null],
  ['ACT_REQ_PICK_DONE', 'REQ_PICKING_ASSIGNED', 'REQ_READY_HANDOVER', 'CMP_REQ_FINISH_PICK', false, false, null],
  ['ACT_REQ_ACCEPT', 'REQ_READY_HANDOVER', 'REQ_COMPLETED', 'CMP_REQ_ACCEPT_HND', false, false, null],
  ['ACT_REQ_CANCEL', 'REQ_DRAFT', 'REQ_CANCELLED', 'CMP_REQ_CANCEL', true, true, null]
]

export type ReqActionName =
  | 'ACT_REQ_SEND'
  | 'ACT_REQ_SHORTAGE_PR'
  | 'ACT_REQ_ASSIGN'
  | 'ACT_REQ_PICK_DONE'
  | 'ACT_REQ_ACCEPT'
  | 'ACT_REQ_CANCEL'

export const reqActionId = (name: ReqActionName) =>
  seedId(SEED_GROUP.wmlAction, scoped(ACTIONS.findIndex(([n]) => n === name) + 1))

export const REQ_ACTION_STATUS = Object.fromEntries(ACTIONS.map(([name, from, to]) => [name, [from, to]])) as Record<
  ReqActionName,
  [ReqStatusCode, ReqStatusCode]
>

const ELIGIBILITY: [ReqActionName, ReqActorCode][] = [
  ['ACT_REQ_SEND', 'ACT_REQ_PRODUKSI'],
  ['ACT_REQ_SHORTAGE_PR', 'ACT_REQ_PRODUKSI'],
  ['ACT_REQ_ASSIGN', 'ACT_REQ_ADMIN_RM'],
  ['ACT_REQ_ASSIGN', 'ACT_REQ_SPV_RM'],
  ['ACT_REQ_ASSIGN', 'ACT_REQ_KADIV_RM'],
  ['ACT_REQ_PICK_DONE', 'ACT_REQ_OP_RM'],
  ['ACT_REQ_ACCEPT', 'ACT_REQ_PRODUKSI'],
  ['ACT_REQ_CANCEL', 'ACT_REQ_PRODUKSI']
]

/** [komponen panel, status, permission, actor]. Baris tanpa actor → semua actor WF-REQ-01 (pola WF-TOB-01). */
const PANELS: [ComponentCode, ReqStatusCode, 'read' | 'write', ReqActorCode | null][] = [
  ['CMP_REQ_PANEL_HEADER', 'REQ_DRAFT', 'write', 'ACT_REQ_PRODUKSI'],
  ['CMP_REQ_PANEL_HEADER', 'REQ_PENDING_ASSIGN', 'read', null],
  ['CMP_REQ_PANEL_ITEMS', 'REQ_DRAFT', 'write', 'ACT_REQ_PRODUKSI'],
  ['CMP_REQ_PANEL_ITEMS', 'REQ_PENDING_ASSIGN', 'read', null],
  ['CMP_REQ_PANEL_HANDOVER', 'REQ_READY_HANDOVER', 'write', 'ACT_REQ_PRODUKSI'],
  ['CMP_REQ_PANEL_HANDOVER', 'REQ_COMPLETED', 'read', null]
]

export function buildWorkflowReqSeed(): SeedTables {
  const panels: LocalRow[] = PANELS.map(([component, status, permission], index) => ({
    ...seedAudit,
    id: seedId(SEED_GROUP.wmlPanel, scoped(index + 1)),
    component_id: componentId(component),
    status_id: reqStatusId(status),
    permission
  }))

  return {
    wml_workflow: [
      {
        ...seedAudit,
        id: WF_REQ_01_ID,
        code: 'WF-REQ-01',
        name: 'Material Request RM Workflow',
        entity: 'material_request',
        source_policy: 'manual_create'
      }
    ],
    wml_status: STATUSES.map(([code, name, order, isStart, isTerminal, color, desc], index) => ({
      ...seedAudit,
      id: seedId(SEED_GROUP.wmlStatus, scoped(index + 1)),
      workflow_id: WF_REQ_01_ID,
      code,
      name,
      order,
      is_start: isStart,
      is_terminal: isTerminal,
      is_send_email: false,
      color,
      desc
    })),
    wml_actor: ACTORS.map(([code, name, role, condition]) => ({
      ...seedAudit,
      id: reqActorId(code),
      workflow_id: WF_REQ_01_ID,
      code,
      name,
      role_id: roleId(role),
      job_fn_id: null,
      condition
    })),
    wml_component: COMPONENTS.map(([code, name, type, uiConfig]) => ({
      ...seedAudit,
      id: componentId(code),
      workflow_id: WF_REQ_01_ID,
      code,
      name,
      type,
      ui_config: uiConfig
    })),
    wml_action: ACTIONS.map(([name, from, to, component, needReason, reasonRequired, patch]) => ({
      ...seedAudit,
      id: reqActionId(name as ReqActionName),
      workflow_id: WF_REQ_01_ID,
      name,
      from_status_id: reqStatusId(from),
      to_status_id: reqStatusId(to),
      component_id: componentId(component),
      is_need_reason: needReason,
      is_reason_required: reasonRequired,
      is_post_closure: false,
      entity_patch_mapping: patch
    })),
    wml_action_actor_eligibility: ELIGIBILITY.map(([action, actor], index) => ({
      ...seedAudit,
      id: seedId(SEED_GROUP.wmlActionActor, scoped(index + 1)),
      action_id: reqActionId(action),
      actor_id: reqActorId(actor)
    })),
    wml_panel: panels,
    wml_panel_actor_eligibility: PANELS.flatMap(([, , , actor], index) =>
      (actor ? [actor] : ACTORS.map(([code]) => code)).map((actorCode, offset) => ({
        ...seedAudit,
        id: seedId(SEED_GROUP.wmlPanelActor, scoped((index + 1) * 10 + offset)),
        panel_id: panels[index].id,
        actor_id: reqActorId(actorCode)
      }))
    )
  }
}
